
import React, { useState, useRef } from 'react';
import { Upload, FileText, FileVideo, FileAudio, X, AlertCircle } from 'lucide-react';
import { UploadMode, FileData } from '../types';

interface FileUploaderProps {
  onProcess: (data: string | FileData, mode: UploadMode) => void;
  isLoading: boolean;
}

const FileUploader: React.FC<FileUploaderProps> = ({ onProcess, isLoading }) => {
  const [mode, setMode] = useState<UploadMode>('text');
  const [text, setText] = useState('');
  const [file, setFile] = useState<FileData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      setFile({
        base64,
        mimeType: selectedFile.type,
        name: selectedFile.name
      });
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleGenerate = () => {
    if (mode === 'text' && text.trim()) {
      onProcess(text, 'text');
    } else if (mode === 'file' && file) {
      onProcess(file, 'file');
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden max-w-4xl mx-auto">
      <div className="flex border-b border-slate-100">
        <button
          onClick={() => setMode('text')}
          className={`flex-1 py-4 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
            mode === 'text' ? 'text-indigo-600 bg-indigo-50/50' : 'text-slate-500 hover:bg-slate-50'
          }`}
        >
          <FileText size={18} />
          Paste Text
        </button>
        <button
          onClick={() => setMode('file')}
          className={`flex-1 py-4 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
            mode === 'file' ? 'text-indigo-600 bg-indigo-50/50' : 'text-slate-500 hover:bg-slate-50'
          }`}
        >
          <Upload size={18} />
          Upload File (Audio/Video/Doc)
        </button>
      </div>

      <div className="p-6">
        {mode === 'text' ? (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste your transcript or message notes here..."
            className="w-full h-64 p-4 text-slate-700 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none transition-all"
          />
        ) : (
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="group cursor-pointer w-full h-64 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 flex flex-col items-center justify-center gap-4 transition-all hover:border-indigo-400 hover:bg-indigo-50/30"
          >
            <input 
              type="file" 
              className="hidden" 
              ref={fileInputRef} 
              onChange={handleFileChange}
              accept="audio/*,video/*,text/plain,application/pdf"
            />
            {file ? (
              <div className="text-center px-4">
                <div className="bg-indigo-600 text-white p-3 rounded-full inline-block mb-3">
                  {file.mimeType.includes('video') ? <FileVideo /> : file.mimeType.includes('audio') ? <FileAudio /> : <FileText />}
                </div>
                <p className="text-indigo-600 font-semibold truncate max-w-xs">{file.name}</p>
                <button 
                  onClick={(e) => { e.stopPropagation(); setFile(null); }} 
                  className="mt-2 text-xs text-slate-400 hover:text-red-500 flex items-center gap-1 mx-auto"
                >
                  <X size={12} /> Remove
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="bg-white p-4 rounded-full shadow-sm mb-4 inline-block group-hover:scale-110 transition-transform">
                  <Upload className="text-indigo-600 w-8 h-8" />
                </div>
                <p className="text-slate-600 font-medium">Click to upload or drag and drop</p>
                <p className="text-slate-400 text-sm mt-1">Audio, Video, PDF, or TXT</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={handleGenerate}
            disabled={isLoading || (mode === 'text' ? !text.trim() : !file)}
            className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
              isLoading || (mode === 'text' ? !text.trim() : !file)
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg hover:shadow-indigo-200'
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Generating Content...
              </>
            ) : (
              'Generate Content'
            )}
          </button>
        </div>

        <div className="mt-4 flex items-start gap-2 text-slate-400 text-xs">
          <AlertCircle size={14} className="mt-0.5" />
          <p>The AI will process your input to extract key points, quotes, and social media captions. Larger files may take a moment.</p>
        </div>
      </div>
    </div>
  );
};

export default FileUploader;
